import { Outlet } from 'react-router-dom';

import { useAppearance } from '@/hooks/useAppearance';
import { useBackButton } from '@/hooks/useBackButton';
import { useLogtoAuth } from '@/hooks/useLogtoAuth';
import { usePresenceHeartbeat } from '@/hooks/usePresenceHeartbeat';
import { useProfileSync } from '@/hooks/useProfileSync';
import { AdminGate } from '@/components/common/AdminGate';
import { Login } from '@/components/common/Login';
import { MainCtaProvider } from '@/contexts/MainCtaContext';
import { MainCta } from './MainCta';
import { TabBar } from './TabBar';

/**
 * Top-level layout: drives Logto auth, wires the BackButton, and renders the
 * active route inside a flex column with a bottom TabBar. While auth is
 * resolving we render a minimal splash; if the user is not authenticated we
 * render the <Login /> CTA instead of the route tree.
 */
export function AppShell() {
  const { isAuthenticated, isLoading } = useLogtoAuth();
  // Theme engine: applies the active palette (CSS vars + Telegram chrome) and
  // persists pref changes. Mounted here so it runs for both Login and routes.
  useAppearance();
  useBackButton();
  // QSocials presence heartbeat — 30s tick while focused, /offline on
  // blur/unload. No-op until the user is authenticated (the hook checks
  // bridgeAuthenticated internally).
  usePresenceHeartbeat();
  // QSocials profile sync — push Telegram displayName + avatar to qsocials
  // once per session so friend/follow lists render real names. No-op until
  // authenticated; the hook checks bridgeAuthenticated internally.
  useProfileSync();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-text">
        <p className="text-fg-muted">Loading…</p>
      </div>
    );
  }

  // Dev escape hatch: when running locally outside of Telegram (or with
  // Logto staging redirect URI not yet whitelisted), append `?dev=1` to the
  // URL to skip the auth gate and preview unauthenticated routes. Authenticated
  // RTK Query calls will fail with 401 — that's expected.
  const devBypass = import.meta.env.DEV && new URLSearchParams(window.location.search).has('dev');

  if (!isAuthenticated && !devBypass) {
    return <Login />;
  }

  return (
    <MainCtaProvider>
      {/* h-[100dvh] (dynamic viewport) clamps the whole shell to the
          *current* visible viewport — bigger than h-screen on iOS because
          it shrinks when the address bar shows up, but never extends past
          what the user can see. min-h-screen would have let content push
          the body taller than the viewport, defeating overflow-hidden on
          routes like Hub that need a strict no-scroll lock. Long routes
          (Tournaments / News) get scroll via <main>'s overflow-y-auto. */}
      <div className="flex h-[100dvh] flex-col overflow-hidden bg-bg text-text">
        {/* AdminGate sits AFTER the auth gate above: unauthenticated users
            still see <Login /> first; only authenticated users hit the admin
            check. When VITE_ADMIN_GATE_ENABLED=false (PROD) it's a pass-through. */}
        <AdminGate>
          <main className="min-h-0 flex-1 overflow-y-auto">
            <Outlet />
          </main>
          <MainCta />
          <TabBar />
        </AdminGate>
      </div>
    </MainCtaProvider>
  );
}
